<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50/30 to-gray-100 dark:from-gray-900 dark:via-blue-950/20 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-blue-400/20 dark:bg-blue-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-purple-400/20 dark:bg-purple-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      <div class="absolute -bottom-8 left-20 w-96 h-96 bg-pink-400/20 dark:bg-pink-500/10 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>
    
    <Navigation />
    
    <!-- Main Content -->
    <section class="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pt-32 md:pt-40 pb-32 md:pb-48">
      <div class="relative">
        <!-- 2-Spalten Layout -->
        <div class="grid grid-cols-1 lg:grid-cols-[40%_60%] gap-8 lg:gap-12 lg:items-center">
          <!-- Linke Spalte: Icon + Service Cards (40%) -->
          <div class="flex flex-col items-center space-y-8">
            <!-- Icon - Mittig -->
            <div class="flex justify-center w-full">
              <div 
                class="relative group transition-all duration-1000 ease-out"
                :class="iconVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'"
              >
                <!-- Glow Effects -->
                <div class="absolute inset-0 bg-blue-500/20 dark:bg-blue-400/15 blur-3xl rounded-full scale-150 group-hover:scale-200 transition-transform duration-700"></div>
                <div class="absolute inset-0 bg-purple-500/15 dark:bg-purple-400/10 blur-2xl rounded-full scale-125 group-hover:scale-150 transition-transform duration-500"></div>
                
                <!-- Main Icon Container -->
                <div class="relative w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 bg-gradient-to-br from-blue-600 to-purple-600 dark:from-blue-500 dark:to-purple-500 rounded-3xl flex items-center justify-center shadow-2xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 border-4 border-white/20 dark:border-gray-700/20">
                  <CodeBracketIcon class="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 text-white transform transition-transform duration-300 group-hover:scale-110" />
                </div>
                
                <!-- Floating Decorative Icons -->
                <div class="absolute -top-2 -right-2 w-10 h-10 bg-blue-500 dark:bg-blue-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 shadow-lg animate-pulse">
                  <SparklesIcon class="w-5 h-5 text-white" />
                </div>
                <div class="absolute -bottom-2 -left-2 w-10 h-10 bg-purple-500 dark:bg-purple-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 shadow-lg animate-pulse" style="transition-delay: 0.1s">
                  <ExclamationTriangleIcon class="w-5 h-5 text-white" />
                </div>
              </div>
            </div>

            <!-- Service Cards -->
            <div 
              class="w-full max-w-xs mx-auto grid grid-cols-1 gap-4 transition-all duration-1000 ease-out"
              :class="cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
              style="transition-delay: 0.7s"
            >
              <!-- WordPress Card -->
              <div class="group relative bg-gradient-to-br from-blue-50 via-blue-100/80 to-white dark:from-blue-900/30 dark:via-blue-800/20 dark:to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border-2 border-blue-200/60 dark:border-blue-700/40 hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer overflow-hidden">
                <!-- Shine Effect -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div class="relative z-10">
                  <div class="flex items-center justify-center mb-4">
                    <div class="w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform duration-300 shadow-lg">
                      <CodeBracketIcon class="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">WordPress</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed text-center">Beratung & Entwicklung</p>
                </div>
              </div>

              <!-- Alternative Lösungen Card -->
              <div class="group relative bg-gradient-to-br from-purple-50 via-purple-100/80 to-white dark:from-purple-900/30 dark:via-purple-800/20 dark:to-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border-2 border-purple-200/60 dark:border-purple-700/40 hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl cursor-pointer overflow-hidden">
                <!-- Shine Effect -->
                <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                
                <div class="relative z-10">
                  <div class="flex items-center justify-center mb-4">
                    <div class="w-14 h-14 bg-gradient-to-br from-purple-600 to-purple-500 dark:from-purple-500 dark:to-purple-600 rounded-2xl flex items-center justify-center group-hover:rotate-6 transition-transform duration-300 shadow-lg">
                      <SparklesIcon class="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2 text-center">Alternative Lösungen</h3>
                  <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed text-center">Moderne Web-Technologien</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Rechte Spalte: Text Block (60%) -->
          <div class="text-center lg:text-left">
            <!-- Haupttext Card mit Glassmorphism -->
            <div 
              class="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-3xl p-8 md:p-12 lg:p-14 shadow-2xl border-2 border-blue-200/60 dark:border-blue-700/60 transition-all duration-1000 ease-out hover:shadow-blue-500/30 dark:hover:shadow-blue-600/30 hover:border-blue-300 dark:hover:border-blue-600"
              :class="contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'"
            >
              <!-- WordPress "Logo" Hinweis (dezent) -->
              <div 
                class="mb-6 flex justify-center lg:justify-start items-center gap-2 transition-all duration-1000 ease-out"
                :class="wpBadgeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'"
                style="transition-delay: 0.2s"
              >
                <div class="px-4 py-2 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-600">
                  <span class="text-sm font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-2">
                    <ExclamationTriangleIcon class="w-4 h-4 text-orange-500" />
                    Kein WordPress
                  </span>
                </div>
              </div>

              <h1 
                class="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 tracking-tight transition-all duration-1000 ease-out leading-tight"
                :class="titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
                style="transition-delay: 0.3s"
              >
                Das ist keine <span class="relative inline-block">
                  <span class="text-blue-600 dark:text-blue-400">WordPress</span>
                  <span class="absolute -bottom-2 left-0 w-full h-2 bg-blue-200/40 dark:bg-blue-400/20 rounded-full -z-10"></span>
                </span>-Seite
              </h1>
              
              <div 
                class="h-1.5 w-24 lg:w-32 bg-gradient-to-r from-blue-600 via-purple-500 to-blue-600 dark:from-blue-400 dark:via-purple-400 dark:to-blue-400 mb-8 lg:mb-10 rounded-full transition-all duration-1000 ease-out shadow-lg"
                :class="[dividerVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0', 'mx-auto lg:mx-0']"
                style="transition-delay: 0.4s"
              ></div>
              
              <div class="space-y-5 lg:space-y-6">
                <p 
                  class="text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-700 dark:text-gray-300 leading-relaxed font-medium transition-all duration-1000 ease-out"
                  :class="text1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
                  style="transition-delay: 0.5s"
                >
                  ...aber bei dieser kann ich dich auch gerne unterstützen
                </p>
                
                <p 
                  class="text-base md:text-lg lg:text-xl xl:text-2xl text-gray-600 dark:text-gray-400 leading-relaxed transition-all duration-1000 ease-out"
                  :class="text2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
                  style="transition-delay: 0.6s"
                >
                  Kontaktiere mich gerne für Beratungen rund um WordPress oder alternative Lösungen
                </p>
              </div>

              <!-- Call to Action Button -->
              <div 
                class="mt-8 lg:mt-10 flex justify-center lg:justify-start transition-all duration-1000 ease-out"
                :class="buttonVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
                style="transition-delay: 0.8s"
              >
                <a 
                  href="/contact"
                  class="group relative inline-flex items-center px-10 py-5 bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-500 dark:to-blue-600 text-white text-lg font-bold rounded-2xl hover:from-blue-700 hover:to-blue-600 dark:hover:from-blue-600 dark:hover:to-blue-700 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl shadow-xl overflow-hidden"
                >
                  <!-- Shimmer Effect -->
                  <span class="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent"></span>
                  
                  <span class="relative z-10">Kontakt aufnehmen</span>
                  <svg class="ml-3 h-5 w-5 relative z-10 transition-transform group-hover:translate-x-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-white py-8 border-t border-gray-200 dark:border-gray-800">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-6">
          <router-link 
            to="/" 
            class="inline-block transition-all duration-300"
            @mouseenter="isFooterNameHovered = true"
            @mouseleave="isFooterNameHovered = false"
            :style="{ transform: isFooterNameHovered ? 'scale(1.05)' : 'scale(1)' }"
          >
            <h3 
              class="text-xl font-semibold mb-2 transition-colors"
              :class="isFooterNameHovered 
                ? 'text-blue-600 dark:text-blue-400' 
                : 'text-gray-900 dark:text-white'"
            >
              RE<span class="it-text it-underline text-blue-600 dark:text-blue-400">IT</span>SBERGER
            </h3>
          </router-link>
          <p class="text-gray-600 dark:text-gray-400 text-sm">IT steckt im Namen</p>
        </div>
        <div class="border-t border-gray-200 dark:border-gray-700 pt-6 text-center">
          <p class="text-gray-600 dark:text-gray-400 text-sm">
            &copy; {{ new Date().getFullYear() }} MAXIMILIAN REITSBERGER. <span class="block sm:inline">Alle Rechte vorbehalten.</span>
          </p>
          <p class="text-gray-500 dark:text-gray-500 text-xs mt-2">
            Senior QA Specialist & Webentwickler | München, Deutschland
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { 
  CodeBracketIcon, 
  SparklesIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import Navigation from '../components/Navigation.vue'

// Footer Hover State
const isFooterNameHovered = ref(false)

// Animation States
const iconVisible = ref(false)
const wpBadgeVisible = ref(false)
const contentVisible = ref(false)
const titleVisible = ref(false)
const dividerVisible = ref(false)
const text1Visible = ref(false)
const text2Visible = ref(false)
const cardsVisible = ref(false)
const buttonVisible = ref(false)

onMounted(() => {
  // Gestaffelte Animationen für besseren visuellen Effekt
  setTimeout(() => {
    iconVisible.value = true
  }, 100)
  
  setTimeout(() => {
    wpBadgeVisible.value = true
  }, 300)
  
  setTimeout(() => {
    contentVisible.value = true
  }, 400)
  
  setTimeout(() => {
    titleVisible.value = true
  }, 500)
  
  setTimeout(() => {
    dividerVisible.value = true
  }, 600)
  
  setTimeout(() => {
    text1Visible.value = true
  }, 700)
  
  setTimeout(() => {
    text2Visible.value = true
  }, 800)
  
  setTimeout(() => {
    cardsVisible.value = true
  }, 900)
  
  setTimeout(() => {
    buttonVisible.value = true
  }, 1000)
})
</script>

<style scoped>
/* Smooth animations */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>

